
import React from 'react'

import { InfoIcon } from 'lucide-react'

import { Box, Paper, Stack, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles';
import type { SxProps } from '@mui/system'



interface CardProps {
  children?: React.ReactNode
  title?: string,
  info?: boolean,
  /** Optional top-right action (e.g. a link/button), aligned with the title. */
  action?: React.ReactNode,
  spacing?: number,
  elevation?: number,
  /**
   * Make the card a flex-fill column: the Paper + its inner content wrappers
   * become `flex:1; minHeight:0` so a `height:100%` child (e.g. a scrollable
   * list) is constrained to the card's bounded height and scrolls inside it
   * instead of overflowing. Opt-in — default off, so normal cards are unchanged.
   */
  fill?: boolean,
  /**
   * Draw the 1px outline. Defaults to TRUE, so every existing card is unchanged.
   *
   * Opt OUT where the card already separates from its backdrop by contrast
   * alone. The auth pages are the first case: in dark mode the border is
   * `grey[100]` (#27272a) on a `#0f0f12` card — a line you can barely see —
   * while the card itself sits as a dark island on a lit purple field, which
   * separates it far more strongly than the outline ever did.
   *
   * Deliberately NOT the default, and deliberately not mode-conditional inside
   * this component. In LIGHT mode the border is load-bearing: `background.paper`
   * is #ffffff on a #f4f5f5 page, a 4% difference, so a borderless card on a
   * pale page has almost no edge at all. Whether that trade is right for the
   * ~100 other call sites is a separate question from whether it is right here,
   * so the choice belongs to the caller until someone decides it globally.
   */
  bordered?: boolean,
  /** Makes the whole card clickable (forwarded to the Paper surface). */
  onClick?: React.MouseEventHandler<HTMLDivElement>,
  /** Forwarded to the Paper surface — e.g. a card that highlights a matching
   *  element elsewhere on hover (purely presentational; never load-bearing). */
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>,
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>,
  /**
   * Keyboard access for a CLICKABLE card. `onClick` alone renders a `div` that no
   * keyboard can reach or activate, so a card used as a navigation target was
   * mouse-only — and any `&:focus-visible` styling on it was dead code, because the
   * element could never take focus. Pass `role="link"` (or `"button"`), `tabIndex={0}`
   * and an `onKeyDown` handling Enter/Space alongside `onClick` to make it real.
   */
  role?: string,
  tabIndex?: number,
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>,
  /**
   * Pointer enter/leave on the card surface.
   *
   * These exist explicitly because this component forwards a NAMED prop list rather
   * than spreading the rest — so any handler not listed here is accepted by the JSX
   * and then silently dropped. That is how a card-hover highlight shipped looking
   * correct and doing nothing: `onMouseEnter` was passed, TypeScript raised nothing
   * (CardProps has no index signature to reject it), and the handler never reached the
   * DOM. If you need another DOM prop on the surface, add it here — do not assume it
   * passes through.
   */
  onMouseEnter?: React.MouseEventHandler<HTMLDivElement>,
  onMouseLeave?: React.MouseEventHandler<HTMLDivElement>,
  sx?: SxProps
}

// Flat by default: in-page surfaces are border + radius, no shadow (consistent
// across light/dark; the border does the separation). Pass elevation > 0 only
// for surfaces that genuinely float above the page.
export const Card = ({ title, info, action, children, sx, spacing = 0, elevation = 0, fill = false, bordered = true, onClick, role, tabIndex, onKeyDown, onMouseEnter, onMouseLeave }: CardProps) => {
  const theme = useTheme()
  const fillSx = fill ? { flex: 1, minHeight: 0 } : undefined

  return (
    <Paper
      elevation={elevation}
      onClick={onClick}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      role={role}
      tabIndex={tabIndex}
      onKeyDown={onKeyDown}
      sx={theme => ({
        border: bordered ? `1px solid ${theme.palette.grey[100]}` : 'none',
        // Cards use the paper surface token (a slight step off the page bg) so they
        // read as surfaces. Field inputs inside cards are white (light mode) so they
        // pop against this grey card rather than blending into it.
        backgroundColor: theme.palette.background.paper,
        padding: 6,
        // NO outer margin. A Card owns its padding; the PARENT owns the gap between
        // it and its neighbours. This used to be `margin: '0 0 12px'`, which made
        // spacing depend on how deeply the Card was nested: MUI's Stack emits its
        // reset (`> :not(style):not(style) { margin: 0 }`) on DIRECT children only,
        // so a Card placed straight into a spaced Stack lost the 12px while an
        // otherwise identical Card one wrapper down kept it — 16px vs 28px between
        // visually identical siblings, with nothing at either call site to explain
        // the difference. Audited across all 100 call sites before removing.
        // Note `spacing={0}` does NOT emit that reset in MUI 5.18 (createStack.js:74
        // gates on `if (ownerState.spacing)`, and 0 is falsy), which is why the rule
        // was so hard to infer from the call sites.
        borderRadius: 3,
        ...(fill && { display: 'flex', flexDirection: 'column', minHeight: 0 }),
        ...sx
      })}
    >
      <Stack direction="column" justifyContent="space-between" sx={fillSx}>
        {(title || info || action) && (
          // `flexWrap` (VRG-643): when the title and the action do not fit on one line (a
          // phone width, or a wide action such as a toggle group) the action drops to its
          // own line instead of running past the card's right edge. On one line nothing
          // changes: `columnGap` is the old `ml: 2` on the action, as a minimum gap.
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            mb={6}
            sx={{ flexWrap: 'wrap', columnGap: 2, rowGap: 3 }}
          >
            <Stack direction="row" alignItems="center">
              {title && (
                <Typography
                  variant="body1"
                  component="h2"
                  sx={{
                    fontFamily: 'Lato, Montserrat, sans-serif',
                    fontSize: '20px',
                    fontWeight: 400,
                    fontStyle: 'normal',
                    lineHeight: '140%',
                    letterSpacing: 0,
                  }}
                >
                  {title}
                </Typography>
              )}
              {info && (
                <InfoIcon size={20} strokeWidth={1} color={theme.palette.primary[400]} style={{marginLeft: '12px' , marginTop: '2px', cursor: 'pointer'}} />
              )}
            </Stack>
            {action && <Box sx={{ flexShrink: 0, maxWidth: '100%' }}>{action}</Box>}
          </Stack>
        )}
        <Stack spacing={spacing} sx={fillSx}>{children}</Stack>
      </Stack>
    </Paper>
  )
}
